import type { CSSProperties, SyntheticEvent } from 'react';
import { useEffect, useState } from 'react';

// Used when a document reports no height of its own, so the frame is never left empty.
const FALLBACK_HEIGHT_PX = 600;

export type ContentSizedIframeResult = {
    /**
     * True while the loaded document's height is still unknown, meaning the iframe is collapsed and
     * hidden — keep a loader up until it turns false so the resize never shows.
     */
    isContentHeightUnknown: boolean;
    /** Attach to the iframe's `onLoad`. */
    onIframeLoad: (event: SyntheticEvent<HTMLIFrameElement>) => void;
    /** Merge into the iframe's `style`. */
    iframeSizingStyle: Pick<CSSProperties, 'height' | 'visibility'>;
};

export const useContentSizedIframe = (html: string): ContentSizedIframeResult => {
    const [heightPx, setHeightPx] = useState<number>();

    useEffect(() => {
        setHeightPx(undefined);
    }, [html]);

    const onIframeLoad = (event: SyntheticEvent<HTMLIFrameElement>) => {
        const doc = event.currentTarget.contentDocument;

        if (!doc) {
            return;
        }

        // the inner document must never scroll on its own
        doc.documentElement.style.overflow = 'hidden';
        setHeightPx(doc.documentElement.scrollHeight || FALLBACK_HEIGHT_PX);
    };

    const isSized = heightPx !== undefined;

    return {
        isContentHeightUnknown: Boolean(html) && !isSized,
        onIframeLoad,
        iframeSizingStyle: { height: heightPx ?? 0, visibility: isSized ? 'visible' : 'hidden' }
    };
};

export default useContentSizedIframe;
